/**
 * e2ee.ts — per-session end-to-end encryption for the console side (SPEC §10).
 *
 * This is the BLIND-RELAY layer: coturn and the broker must see ciphertext only.
 *
 * It interoperates byte-for-byte with the agent's Noise_XK implementation in
 * agent/internal/e2ee/e2ee.go. The console is the Noise INITIATOR (technician);
 * the agent is the RESPONDER whose static key is its device key. The handshake is
 *
 *     -> e, es        (msg1, initiator → responder)   kind="e2ee_init"
 *     <- e, ee        (msg2, responder → initiator)   kind="e2ee_resp"
 *     -> s, se        (msg3, initiator → responder)   kind="e2ee_final"
 *
 * After it completes, both sides derive two transport keys via split() and wrap
 * every subsequent data-channel payload with ChaCha20-Poly1305 (per-message
 * counter nonce). The wire layout matches the agent's Session.Seal:
 *
 *     [8-byte big-endian counter][ciphertext+tag]
 *
 * Primitives: X25519 + HKDF-SHA256 + SHA-256 via WebCrypto; ChaCha20-Poly1305
 * from @noble/ciphers (never hand-rolled).
 *
 * TODO(hardware): the media SFrame transform below is a hook only. Full SFrame
 * keying (NAL-unit boundaries, key ratcheting, IDR alignment) needs validation
 * against the agent's real frame encryptor on physical H.264 streams. Until then
 * the transform is pass-through unless a session key is installed, in which case
 * it applies the documented placeholder AEAD. See README.md.
 */

import { chacha20poly1305 } from '@noble/ciphers/chacha';
import { toArrayBuffer } from '@/lib/binary';

const PROTOCOL_NAME = 'Noise_XK_25519_ChaChaPoly_SHA256'; // exactly 32 bytes
const DH_LEN = 32;
const TAG_LEN = 16;

// ---------------------------------------------------------------------------
// Low-level crypto helpers (match the Go side exactly)
// ---------------------------------------------------------------------------

async function sha256(...parts: Uint8Array[]): Promise<Uint8Array> {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const buf = new Uint8Array(total);
  let o = 0;
  for (const p of parts) {
    buf.set(p, o);
    o += p.length;
  }
  const d = await crypto.subtle.digest('SHA-256', toArrayBuffer(buf));
  return new Uint8Array(d);
}

/**
 * HKDF(SHA-256) reading `length` bytes, matching Go's
 * hkdf.New(sha256.New, ikm, salt, info): Extract(salt, ikm) then Expand(info).
 */
async function hkdf(salt: Uint8Array, ikm: Uint8Array, length: number): Promise<Uint8Array> {
  const base = await crypto.subtle.importKey('raw', toArrayBuffer(ikm), 'HKDF', false, [
    'deriveBits',
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: toArrayBuffer(salt), info: new Uint8Array(0) },
    base,
    length * 8,
  );
  return new Uint8Array(bits);
}

/** X25519 raw scalar multiplication via WebCrypto. */
async function x25519(privateKey: CryptoKey, peerPublicRaw: Uint8Array): Promise<Uint8Array> {
  const pub = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(peerPublicRaw),
    { name: 'X25519' },
    false,
    [],
  );
  const bits = await crypto.subtle.deriveBits({ name: 'X25519', public: pub }, privateKey, 256);
  return new Uint8Array(bits);
}

interface X25519KeyPair {
  privateKey: CryptoKey;
  publicRaw: Uint8Array;
}

async function generateX25519(): Promise<X25519KeyPair> {
  const kp = (await crypto.subtle.generateKey({ name: 'X25519' }, true, [
    'deriveBits',
  ])) as CryptoKeyPair;
  const publicRaw = new Uint8Array(await crypto.subtle.exportKey('raw', kp.publicKey));
  return { privateKey: kp.privateKey, publicRaw };
}

// Handshake nonce: 4 zero bytes || little-endian uint64 counter (matches Go's
// symmetricState.nonce()).
function handshakeNonce(n: number): Uint8Array {
  const nb = new Uint8Array(12);
  const view = new DataView(nb.buffer);
  // n is small (handshake), but keep the full 64-bit LE layout for parity.
  view.setUint32(4, n >>> 0, true);
  view.setUint32(8, Math.floor(n / 0x1_0000_0000) >>> 0, true);
  return nb;
}

// ---------------------------------------------------------------------------
// Noise symmetric state (mirror of agent symmetricState)
// ---------------------------------------------------------------------------

class SymmetricState {
  ck: Uint8Array;
  h: Uint8Array;
  k: Uint8Array = new Uint8Array(32);
  n = 0;
  hasKey = false;

  private constructor(h: Uint8Array) {
    this.h = h;
    this.ck = h.slice();
  }

  static create(): SymmetricState {
    const name = new TextEncoder().encode(PROTOCOL_NAME);
    const h = new Uint8Array(32);
    // PROTOCOL_NAME is exactly 32 bytes, so it is copied directly (not hashed).
    h.set(name.subarray(0, 32));
    return new SymmetricState(h);
  }

  async mixHash(data: Uint8Array): Promise<void> {
    this.h = await sha256(this.h, data);
  }

  async mixKey(input: Uint8Array): Promise<void> {
    const out = await hkdf(this.ck, input, 64);
    this.ck = out.slice(0, 32);
    this.k = out.slice(32, 64);
    this.n = 0;
    this.hasKey = true;
  }

  async encryptAndHash(plaintext: Uint8Array): Promise<Uint8Array> {
    if (!this.hasKey) {
      await this.mixHash(plaintext);
      return plaintext;
    }
    const aead = chacha20poly1305(this.k, handshakeNonce(this.n), this.h);
    const ct = aead.encrypt(plaintext);
    this.n += 1;
    await this.mixHash(ct);
    return ct;
  }

  async decryptAndHash(ciphertext: Uint8Array): Promise<Uint8Array> {
    if (!this.hasKey) {
      await this.mixHash(ciphertext);
      return ciphertext;
    }
    const aead = chacha20poly1305(this.k, handshakeNonce(this.n), this.h);
    const pt = aead.decrypt(ciphertext);
    this.n += 1;
    await this.mixHash(ciphertext);
    return pt;
  }

  /**
   * Two transport keys (k1 = initiator→responder, k2 = responder→initiator) plus a
   * dedicated MEDIA key. Reads 96 bytes (was 64) so k1/k2 are byte-identical to the
   * prior derivation; the extra 32 bytes are kMedia, used only for agent→viewer
   * screen frames. MUST stay byte-identical to the agent's split (96-byte read).
   */
  async split(): Promise<{ k1: Uint8Array; k2: Uint8Array; kMedia: Uint8Array }> {
    const out = await hkdf(this.ck, new Uint8Array(0), 96);
    return { k1: out.slice(0, 32), k2: out.slice(32, 64), kMedia: out.slice(64, 96) };
  }
}

// ---------------------------------------------------------------------------
// Noise_XK initiator handshake
// ---------------------------------------------------------------------------

/**
 * The technician-side (initiator) Noise_XK handshake. `remoteStatic` is the
 * agent's known device public key (pinned out-of-band, SPEC §10.1).
 *
 * For the buildable dev path the initiator uses an ephemeral X25519 static key
 * (no long-lived technician static); the agent authenticates the *channel*, and
 * the session JWT proven on the signaling socket authenticates the technician.
 */
export class NoiseInitiator {
  private sym = SymmetricState.create();
  private e!: X25519KeyPair;
  private s!: X25519KeyPair;
  private re: Uint8Array | null = null;
  private readonly rs: Uint8Array;
  private done = false;

  private constructor(remoteStatic: Uint8Array) {
    this.rs = remoteStatic;
  }

  static async create(remoteStatic: Uint8Array): Promise<NoiseInitiator> {
    const hs = new NoiseInitiator(remoteStatic);
    hs.e = await generateX25519();
    hs.s = await generateX25519();
    await hs.sym.mixHash(remoteStatic); // pre-message: responder static
    return hs;
  }

  /** Produce msg1 (e, es). Sent as kind="e2ee_init". */
  async writeMsg1(): Promise<Uint8Array> {
    await this.sym.mixHash(this.e.publicRaw);
    const ss = await x25519(this.e.privateKey, this.rs);
    await this.sym.mixKey(ss);
    const ct = await this.sym.encryptAndHash(new Uint8Array(0));
    return concat(this.e.publicRaw, ct);
  }

  /** Consume msg2 (e, ee). Received as kind="e2ee_resp". */
  async readMsg2(msg: Uint8Array): Promise<void> {
    if (msg.length < DH_LEN) throw new Error('e2ee: msg2 too short');
    this.re = msg.slice(0, DH_LEN);
    await this.sym.mixHash(this.re);
    const ss = await x25519(this.e.privateKey, this.re);
    await this.sym.mixKey(ss);
    await this.sym.decryptAndHash(msg.subarray(DH_LEN));
  }

  /** Produce msg3 (s, se). Sent as kind="e2ee_final". Completes the handshake. */
  async writeMsg3(): Promise<Uint8Array> {
    if (!this.re) throw new Error('e2ee: writeMsg3 before msg2');
    const encS = await this.sym.encryptAndHash(this.s.publicRaw);
    const ss = await x25519(this.s.privateKey, this.re);
    await this.sym.mixKey(ss);
    const ct = await this.sym.encryptAndHash(new Uint8Array(0));
    this.done = true;
    return concat(encS, ct);
  }

  /** Derive the transport session. Initiator sends with k1, receives with k2. */
  async session(): Promise<SessionKeys> {
    if (!this.done) throw new Error('e2ee: handshake not complete');
    const { k1, k2, kMedia } = await this.sym.split();
    return {
      txKey: k1,
      rxKey: k2,
      txCounter: { n: 0 },
      rxWindow: new ReplayWindow(),
      mediaKey: kMedia,
      mediaWindow: new ReplayWindow(),
    };
  }
}

// ---------------------------------------------------------------------------
// Transport keys + seal/open (mirror of agent e2ee.Session)
// ---------------------------------------------------------------------------

const REPLAY_WINDOW_BITS = 4096;

/**
 * RFC 6479-style sliding-window anti-replay over the agent's monotonic send
 * counter. The agent's payloads ride six independent SCTP data-channel streams
 * sharing ONE send counter, so they arrive out of order relative to the counter;
 * a strict high-water watermark would silently drop the reordered ones. This
 * accepts any not-yet-seen counter within REPLAY_WINDOW_BITS of the high-water
 * mark and rejects replays / stale frames. Mirrors the agent's replayWindow in
 * agent/internal/e2ee/e2ee.go so both ends have the same protection + tolerance.
 */
export class ReplayWindow {
  private hi = 0;
  private started = false;
  private readonly bits = new Uint32Array(REPLAY_WINDOW_BITS / 32);

  /** Records counter n; returns true if fresh (accept), false if replay/stale. */
  check(n: number): boolean {
    const W = REPLAY_WINDOW_BITS;
    if (!this.started) {
      this.started = true;
      this.hi = n;
      this.set(n);
      return true;
    }
    if (n > this.hi) {
      // Advance; clear the freshly-uncovered slots so a previous-lap counter
      // (n-W maps to the same ring slot as n) can't alias as already-seen.
      if (n - this.hi >= W) {
        this.bits.fill(0);
      } else {
        for (let i = this.hi + 1; i <= n; i++) this.clear(i);
      }
      this.hi = n;
      this.set(n);
      return true;
    }
    if (this.hi - n >= W) return false; // stale: older than the window
    if (this.get(n)) return false; // replay: already seen
    this.set(n);
    return true;
  }

  private set(n: number): void {
    const p = n % REPLAY_WINDOW_BITS;
    this.bits[p >>> 5] |= 1 << (p & 31);
  }
  private clear(n: number): void {
    const p = n % REPLAY_WINDOW_BITS;
    this.bits[p >>> 5] &= ~(1 << (p & 31));
  }
  private get(n: number): boolean {
    const p = n % REPLAY_WINDOW_BITS;
    return (this.bits[p >>> 5] & (1 << (p & 31))) !== 0;
  }
}

/** Session keys derived from the completed Noise handshake. */
export interface SessionKeys {
  /** key for messages we (technician/initiator) send → agent */
  txKey: Uint8Array;
  /** key for messages the agent sends → us */
  rxKey: Uint8Array;
  /** monotonically-increasing send counter (8-byte BE nonce, matches agent) */
  txCounter: { n: number };
  /** anti-replay over received counters (data channels) */
  rxWindow: ReplayWindow;
  /** dedicated key for agent→viewer media (screen) frames; receive-only here */
  mediaKey: Uint8Array;
  /** anti-replay over the media counter space (separate from rxWindow) */
  mediaWindow: ReplayWindow;
}

// Transport nonce: 4 zero bytes || big-endian uint64 counter (agent
// e2ee.counterNonce). The counter is also prefixed to the wire.
function transportNonce(n: number): Uint8Array {
  const nb = new Uint8Array(12);
  const view = new DataView(nb.buffer);
  view.setUint32(4, Math.floor(n / 0x1_0000_0000) >>> 0, false);
  view.setUint32(8, n >>> 0, false);
  return nb;
}

/**
 * Seal a data-channel payload. Wire = [8-byte BE counter][ciphertext+tag]
 * (exactly the agent's Session.Seal). AD is empty (matches the agent's nil AAD).
 */
export function sealData(keys: SessionKeys, plaintext: Uint8Array, aad?: Uint8Array): Uint8Array {
  const n = keys.txCounter.n;
  keys.txCounter.n += 1;
  const aead = chacha20poly1305(keys.txKey, transportNonce(n), aad);
  const ct = aead.encrypt(plaintext);
  const out = new Uint8Array(8 + ct.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, Math.floor(n / 0x1_0000_0000) >>> 0, false);
  view.setUint32(4, n >>> 0, false);
  out.set(ct, 8);
  return out;
}

/**
 * Open a sealed payload produced by the agent (decrypt only, NO anti-replay).
 * Throws on auth failure. Used by the media transform (separate counter space);
 * for the structured DATA CHANNELS use openDataChecked so replays are rejected.
 */
export function openData(keys: SessionKeys, framed: Uint8Array, aad?: Uint8Array): Uint8Array {
  if (framed.length < 8) throw new Error('e2ee: short sealed message');
  const view = new DataView(framed.buffer, framed.byteOffset, framed.byteLength);
  const hi = view.getUint32(0, false);
  const lo = view.getUint32(4, false);
  const n = hi * 0x1_0000_0000 + lo;
  const aead = chacha20poly1305(keys.rxKey, transportNonce(n), aad);
  return aead.decrypt(framed.subarray(8));
}

/**
 * Open a sealed data-channel payload AND enforce anti-replay. The sliding-window
 * check runs only AFTER a successful AEAD decrypt, so a forged tag with an
 * attacker-chosen counter cannot perturb the window (matches the agent). Throws
 * on auth failure or on a replayed/stale counter. This is the blind-relay
 * defense: an untrusted TURN relay must not be able to replay agent→console
 * frames (re-injecting clipboard/sysinfo/file bytes).
 */
export function openDataChecked(
  keys: SessionKeys,
  framed: Uint8Array,
  aad?: Uint8Array,
): Uint8Array {
  if (framed.length < 8) throw new Error('e2ee: short sealed message');
  const view = new DataView(framed.buffer, framed.byteOffset, framed.byteLength);
  const n = view.getUint32(0, false) * 0x1_0000_0000 + view.getUint32(4, false);
  const aead = chacha20poly1305(keys.rxKey, transportNonce(n), aad);
  const pt = aead.decrypt(framed.subarray(8));
  if (!keys.rxWindow.check(n)) {
    throw new Error(`e2ee: replayed or stale counter ${n}`);
  }
  return pt;
}

/**
 * Open a sealed MEDIA frame (agent→viewer screen video) produced by the agent's
 * Session.SealMedia. Wire layout is identical to a data-channel frame —
 * [8-byte BE counter][ciphertext+tag] — but it uses the dedicated media key and a
 * SEPARATE replay window, so media and data-channel frames never share a
 * (key, nonce) pair. Throws on auth failure or a replayed/stale media counter.
 * Byte-compatible with agent SealMedia (verified by the cross-language fixture).
 */
export function openMedia(keys: SessionKeys, framed: Uint8Array): Uint8Array {
  if (framed.length < 8) throw new Error('e2ee: short media frame');
  const view = new DataView(framed.buffer, framed.byteOffset, framed.byteLength);
  const n = view.getUint32(0, false) * 0x1_0000_0000 + view.getUint32(4, false);
  const aead = chacha20poly1305(keys.mediaKey, transportNonce(n));
  const pt = aead.decrypt(framed.subarray(8));
  if (!keys.mediaWindow.check(n)) {
    throw new Error(`e2ee: replayed or stale media counter ${n}`);
  }
  return pt;
}

function concat(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

// expose for tests / callers that need the constants
export const E2EE_CONSTANTS = { DH_LEN, TAG_LEN, PROTOCOL_NAME };

// ---------------------------------------------------------------------------
// C1 — cert-bind the agent Noise key (CA-root attestation, SPEC §10.1)
//
// A malicious/compromised broker can substitute the agent's Noise X25519 static
// key and MITM "encrypted" sessions. The CA root signs (device_id, noise_pubkey)
// and the console verifies that attestation against an OUT-OF-BAND-pinned CA
// root before trusting the key. This module only VERIFIES; the pin decision and
// fail-closed policy live in session.ts.
// ---------------------------------------------------------------------------

/** Domain-separation prefix for the binding message (must match the server). */
const NOISE_BINDING_PREFIX = 'helm-noise-binding-v1:';

/** Decode a lowercase/uppercase hex string to bytes; throws on malformed hex. */
function hexToBytesStrict(hex: string): Uint8Array {
  const clean = hex.trim();
  if (clean.length % 2 !== 0) throw new Error('e2ee: odd-length hex');
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    const byte = parseInt(clean.substr(i * 2, 2), 16);
    if (Number.isNaN(byte)) throw new Error('e2ee: invalid hex');
    out[i] = byte;
  }
  return out;
}

/**
 * Verify the CA-root attestation over (deviceId, noisePubKeyHex), SPEC §10.1.
 *
 * The attestation is ECDSA-P256(rootKey, SHA-256(message)) where message is the
 * EXACT UTF-8 bytes of:
 *
 *     "helm-noise-binding-v1:" + deviceId + ":" + noisePubKeyHex
 *
 * The signature is RAW r||s (P1363, 64 bytes) — NOT ASN.1 DER — which is exactly
 * what WebCrypto subtle.verify consumes. rootPubKeyHex is a hex DER SPKI
 * (ECDSA P-256). Fails CLOSED: any malformed input or verify error → false.
 *
 * Note on `crypto`: this resolves to the environment WebCrypto (browser
 * window.crypto, or Node 20 webcrypto exposed as globalThis.crypto under jsdom),
 * the same binding the rest of this module uses.
 */
export async function verifyNoiseAttestation(
  deviceId: string,
  noisePubKeyHex: string,
  attestationHex: string,
  rootPubKeyHex: string,
): Promise<boolean> {
  try {
    if (!deviceId || !noisePubKeyHex || !attestationHex || !rootPubKeyHex) return false;

    const attestation = hexToBytesStrict(attestationHex);
    // RAW P1363 ECDSA P-256 signature is r||s = 32 + 32 bytes. Reject anything else.
    if (attestation.length !== 64) return false;

    // X25519 static key is 32 bytes. Reject a wrong-length pubkey before we sign
    // a meaningless message (defense in depth; the API returns 32-byte hex).
    const noisePub = hexToBytesStrict(noisePubKeyHex);
    if (noisePub.length !== DH_LEN) return false;

    const der = hexToBytesStrict(rootPubKeyHex);

    const key = await crypto.subtle.importKey(
      'spki',
      toArrayBuffer(der),
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify'],
    );

    const message = new TextEncoder().encode(
      `${NOISE_BINDING_PREFIX}${deviceId}:${noisePubKeyHex}`,
    );

    return await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      key,
      toArrayBuffer(attestation),
      toArrayBuffer(message),
    );
  } catch {
    // Fail closed on ANY error (bad hex, unsupported curve, import failure, ...).
    return false;
  }
}

/**
 * The out-of-band-pinned CA root public key (hex DER SPKI), or null if the
 * deployment has not pinned one. Read from build-time env (VITE_HELM_CA_ROOT).
 * When set, session.ts hard-fails any session whose served root or attestation
 * does not match (fail closed). When null, the served root is used best-effort
 * (dev/unconfigured) and an unverified key is allowed with a loud warning.
 */
export function pinnedCARoot(): string | null {
  const pin = import.meta.env.VITE_HELM_CA_ROOT;
  if (typeof pin === 'string' && pin.trim().length > 0) return pin.trim();
  return null;
}

/**
 * Whether the deployment REQUIRES an out-of-band pin (opt-in, VITE_HELM_REQUIRE_PIN).
 * When true and no `VITE_HELM_CA_ROOT` is pinned, session.ts refuses to start any
 * session (fail closed) — the strong E2EE-vs-server posture. Default FALSE so a
 * fresh self-hosted deployment that hasn't set up out-of-band pinning is still
 * usable: it verifies the agent key against the SERVED CA root (which defends
 * against a compromised RELAY) and warns that an out-of-band pin is needed to also
 * defend against a compromised server.
 */
export function requirePinEnforced(): boolean {
  const v = String(import.meta.env.VITE_HELM_REQUIRE_PIN ?? '').trim().toLowerCase();
  return v === 'true' || v === '1' || v === 'yes' || v === 'on';
}

// ---------------------------------------------------------------------------
// Media E2EE — WebRTC Encoded Transform hook (SPEC §10.3)
// ---------------------------------------------------------------------------

/** Feature-detect Encoded Transform (insertable streams) support. */
export function supportsEncodedTransform(): boolean {
  return (
    typeof (globalThis as unknown as { RTCRtpScriptTransform?: unknown }).RTCRtpScriptTransform !==
      'undefined' ||
    'createEncodedStreams' in RTCRtpReceiver.prototype
  );
}

/**
 * Attach the media-decrypt transform to a receiver. Until a session key is
 * installed the transform is pass-through; once installed it strips the key-id
 * prefix and (placeholder) decrypts the frame payload.
 *
 * TODO(hardware): replace the placeholder per-frame AEAD with the agreed SFrame
 * key schedule and validate against the agent encryptor on real H.264 streams.
 */
export function attachMediaDecryptor(
  receiver: RTCRtpReceiver,
  getKeys: () => SessionKeys | null,
): void {
  const RtcScriptTransform = (
    globalThis as unknown as {
      RTCRtpScriptTransform?: new (worker: Worker, opts: unknown) => unknown;
    }
  ).RTCRtpScriptTransform;
  if (RtcScriptTransform) {
    // TODO(hardware): ship the transform Worker + SFrame key channel.
  }

  type Insertable = RTCRtpReceiver & {
    createEncodedStreams?: () => {
      readable: ReadableStream<RTCEncodedVideoFrame>;
      writable: WritableStream<RTCEncodedVideoFrame>;
    };
  };
  const r = receiver as Insertable;
  if (typeof r.createEncodedStreams !== 'function') return;

  const { readable, writable } = r.createEncodedStreams();
  const transform = new TransformStream<RTCEncodedVideoFrame, RTCEncodedVideoFrame>({
    transform(frame, controller) {
      const keys = getKeys();
      if (!keys) {
        controller.enqueue(frame);
        return;
      }
      try {
        const data = new Uint8Array(frame.data);
        // Placeholder framing: [1-byte keyId][nonce||ct]. Real SFrame keying TBD.
        if (data.length > 1) {
          const payload = data.subarray(1);
          const clear = openData(keys, payload);
          frame.data = toArrayBuffer(clear);
        }
        controller.enqueue(frame);
      } catch {
        // Drop undecryptable frames rather than render garbage.
      }
    },
  });
  readable.pipeThrough(transform).pipeTo(writable).catch(() => {
    /* receiver closed */
  });
}
